import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";
import { PaymentMethod } from "model/Gig";
import type {
  AddStaffFormData,
  AddStaffValidationRequirements,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import { isPaymentMethodValueValidOrEmpty } from "../shared/staff.paymentMethod.logic";
import { getEmploymentFieldVisibility } from "../shared/staffEmploymentFieldVisibility.logic";
import {
  hasValidEmployeeNumber,
  hasValidName,
  hasValidOptionalEmail,
  hasValidOptionalEmploymentDays,
  hasValidOptionalPersonalIdentityNumber,
  hasValidOptionalText,
  hasValidPhoneNumber,
  hasValidRequiredText,
  hasValidWorkPercentage as isValidWorkPercentage,
} from "../shared/staffInputValidation.logic";

const MONTHLY_SALARY_TYPE = "Monthly";

interface AddStaffValidationParams {
  readonly canSelectSimflexityEmployee?: boolean;
  readonly canSelectSimflexityPay?: boolean;
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}

interface ContractValidationValues {
  readonly hasValidAgreement: boolean;
  readonly hasValidNoticeDays: boolean;
  readonly hasValidVacationDays: boolean;
  readonly hasValidWorkplace: boolean;
}

interface EmploymentValidationValues extends ContractValidationValues {
  readonly hasValidEmployeeNumber: boolean;
  readonly hasValidPaymentMethod: boolean;
  readonly hasValidSalaryAmount: boolean;
  readonly hasValidWorkPercentage: boolean;
}

interface EmploymentTypeFlags {
  readonly isConsultant: boolean;
  readonly isProjectEmployee: boolean;
  readonly isSimflexityEmployee: boolean;
  readonly isTrainee: boolean;
}

interface EmploymentInputValidationParams {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
  readonly validationValues: EmploymentValidationValues;
}

interface RequiredEmploymentValidationParams extends EmploymentInputValidationParams {
  readonly canSelectSimflexityEmployee: boolean;
  readonly canSelectSimflexityPay: boolean;
}

function isPositiveNumber(value?: string): boolean {
  if (!value || value.trim().length === 0) return false;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
}

function hasNonEmptyValue(value?: string): boolean {
  return Boolean(value && value.trim().length > 0);
}

function normalizeEmploymentType(
  employmentType: AddStaffFormData["employmentType"],
): string {
  return (employmentType ?? "").trim().toLowerCase();
}

function hasInvalidDateRange(formData: AddStaffFormData): boolean {
  if (!formData.startDate || !formData.endDate) return false;
  return !formData.endDate.isAfter(formData.startDate);
}

function isSimflexityEmployee(formData: AddStaffFormData): boolean {
  return (
    normalizeEmploymentType(formData.employmentType) ===
    EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE.toLowerCase()
  );
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  return hasValidPhoneNumber({ value: phoneNumber });
}

function getEmploymentTypeFlags(
  formData: AddStaffFormData,
): EmploymentTypeFlags {
  const employmentType = normalizeEmploymentType(formData.employmentType);
  return {
    isConsultant: employmentType === EMPLOYMENT_TYPE_CONSULTANT.toLowerCase(),
    isProjectEmployee:
      employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE.toLowerCase(),
    isSimflexityEmployee:
      employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE.toLowerCase(),
    isTrainee: employmentType === EMPLOYMENT_TYPE_TRAINEE.toLowerCase(),
  };
}

export function getAddStaffValidationRequirements(
  formData: AddStaffFormData,
): AddStaffValidationRequirements {
  const employmentTypeFlags = getEmploymentTypeFlags(formData);
  const isEmployeeNumberRequired =
    !employmentTypeFlags.isConsultant &&
    !employmentTypeFlags.isTrainee &&
    !employmentTypeFlags.isSimflexityEmployee;
  // Hidden fields are never required, so requirements follow field visibility.
  const visibility = getEmploymentFieldVisibility(formData.employmentType);

  return {
    isEmployeeNumberRequired,
    isEmployeeCategoryRequired: visibility.showEmployeeCategory,
    isSalaryModelRequired: visibility.showSalaryType,
    isSalaryAmountRequired: visibility.showSalary,
    isWorkPercentageRequired:
      visibility.showSalaryType && formData.salaryType === MONTHLY_SALARY_TYPE,
    isTaxTableRequired: visibility.showTaxTable,
    isPaymentTypeRequired: visibility.showPaymentType,
    isEndDateRequired:
      employmentTypeFlags.isProjectEmployee || employmentTypeFlags.isTrainee,
  };
}

function hasRequiredPersonalValues(formData: AddStaffFormData): boolean {
  if (!hasValidName({ value: formData.firstName })) return false;
  if (!hasValidName({ value: formData.lastName })) return false;
  if (!hasValidRequiredText({ value: formData.role })) return false;
  if (!hasValidOptionalEmail({ value: formData.email })) return false;
  if (
    !hasValidOptionalPersonalIdentityNumber({
      value: formData.personalIdentityNumber,
    })
  )
    return false;
  return formData.teamIds.length > 0;
}

/**
 * Company policy rules:
 * - Starter subscriptions cannot create SimflexityEmployee.
 * - SimflexityEmployee cannot use Monthly salary model and must use SimflexityPay.
 * - Other employment types can only use SimflexityPay if policy allows it.
 */
function hasValidCompanyPolicyFields({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
}: {
  readonly canSelectSimflexityEmployee: boolean;
  readonly canSelectSimflexityPay: boolean;
  readonly formData: AddStaffFormData;
}): boolean {
  const isSimflexity = isSimflexityEmployee(formData);

  if (isSimflexity && !canSelectSimflexityEmployee) return false;
  if (isSimflexity && formData.salaryType === MONTHLY_SALARY_TYPE) return false;
  if (isSimflexity && formData.paymentType !== PaymentMethod.SIMFLEXITY_PAY) {
    return false;
  }
  if (!isSimflexity && formData.paymentType === PaymentMethod.SIMFLEXITY_PAY) {
    return canSelectSimflexityPay;
  }

  return true;
}

function hasRequiredEmploymentType(formData: AddStaffFormData): boolean {
  return hasNonEmptyValue(formData.employmentType);
}

function hasRequiredSalaryFields({
  formData,
  hasValidSalaryAmount,
  hasValidWorkPercentage,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidSalaryAmount: boolean;
  readonly hasValidWorkPercentage: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (
    requirements.isSalaryModelRequired &&
    !hasNonEmptyValue(formData.salaryType)
  ) {
    return false;
  }

  if (requirements.isSalaryAmountRequired && !hasValidSalaryAmount)
    return false;

  if (!hasValidWorkPercentage) return false;

  return true;
}

function hasRequiredCompensationFields({
  formData,
  hasValidPaymentMethod,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidPaymentMethod: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (requirements.isTaxTableRequired && !hasNonEmptyValue(formData.taxTable))
    return false;

  if (!hasValidPaymentMethod) return false;

  if (
    requirements.isPaymentTypeRequired &&
    !hasNonEmptyValue(formData.paymentType)
  ) {
    return false;
  }

  return true;
}

function hasRequiredClassificationFields({
  formData,
  hasValidEmployeeNumber: isEmployeeNumberValid,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidEmployeeNumber: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (
    requirements.isEmployeeCategoryRequired &&
    !hasNonEmptyValue(formData.employeeCategory)
  ) {
    return false;
  }

  if (!isEmployeeNumberValid) return false;

  return true;
}

function hasRequiredDateFields({
  formData,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (!formData.startDate) return false;

  if (requirements.isEndDateRequired && !formData.endDate) return false;

  if (formData.endDate && hasInvalidDateRange(formData)) return false;

  return true;
}

function hasRequiredEmploymentValues({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
  requirements,
  validationValues,
}: RequiredEmploymentValidationParams): boolean {
  return (
    hasRequiredEmploymentInputs({
      formData,
      requirements,
      validationValues,
    }) &&
    hasRequiredCompensationInputs({
      canSelectSimflexityEmployee,
      canSelectSimflexityPay,
      formData,
      requirements,
      validationValues,
    }) &&
    hasValidContractValues(validationValues)
  );
}

function hasRequiredEmploymentInputs({
  formData,
  requirements,
  validationValues,
}: EmploymentInputValidationParams): boolean {
  return (
    hasRequiredEmploymentType(formData) &&
    hasRequiredClassificationFields({
      formData,
      hasValidEmployeeNumber: validationValues.hasValidEmployeeNumber,
      requirements,
    }) &&
    hasRequiredDateFields({ formData, requirements })
  );
}

function hasRequiredCompensationInputs({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
  requirements,
  validationValues,
}: RequiredEmploymentValidationParams): boolean {
  return (
    hasRequiredSalaryFields({
      formData,
      hasValidSalaryAmount: validationValues.hasValidSalaryAmount,
      hasValidWorkPercentage: validationValues.hasValidWorkPercentage,
      requirements,
    }) &&
    hasRequiredCompensationFields({
      formData,
      hasValidPaymentMethod: validationValues.hasValidPaymentMethod,
      requirements,
    }) &&
    hasValidCompanyPolicyFields({
      canSelectSimflexityEmployee,
      canSelectSimflexityPay,
      formData,
    })
  );
}

function hasValidContractValues(
  validationValues: EmploymentValidationValues,
): boolean {
  return (
    validationValues.hasValidAgreement &&
    validationValues.hasValidNoticeDays &&
    validationValues.hasValidVacationDays &&
    validationValues.hasValidWorkplace
  );
}

function getContractValidationValues(
  formData: AddStaffFormData,
): ContractValidationValues {
  const visibility = getEmploymentFieldVisibility(formData.employmentType);
  const shouldValidateContractTerms = visibility.showContractTerms;
  return {
    hasValidAgreement:
      !shouldValidateContractTerms ||
      hasValidOptionalText({ value: formData.agreement }),
    hasValidNoticeDays:
      !shouldValidateContractTerms ||
      hasValidOptionalEmploymentDays({ value: formData.noticeDays }),
    hasValidVacationDays:
      !shouldValidateContractTerms ||
      hasValidOptionalEmploymentDays({ value: formData.vacationDays }),
    hasValidWorkplace:
      !shouldValidateContractTerms ||
      hasValidOptionalText({ value: formData.workplace }),
  };
}

function getEmploymentValidationValues({
  formData,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}): EmploymentValidationValues {
  return {
    ...getContractValidationValues(formData),
    hasValidEmployeeNumber: hasValidEmployeeNumber({
      isRequired: requirements.isEmployeeNumberRequired,
      value: formData.employeeNumber,
    }),
    hasValidPaymentMethod: isPaymentMethodValueValidOrEmpty(
      formData.paymentMethodType,
      formData.paymentMethodValue,
    ),
    hasValidSalaryAmount: isPositiveNumber(formData.salaryAmount),
    hasValidWorkPercentage:
      !requirements.isWorkPercentageRequired ||
      isValidWorkPercentage({ value: formData.workPercentage }),
  };
}

export function getAddStaffValidationState({
  canSelectSimflexityEmployee = true,
  canSelectSimflexityPay = false,
  formData,
  requirements,
}: AddStaffValidationParams): AddStaffValidationState {
  const validationValues = getEmploymentValidationValues({
    formData,
    requirements,
  });
  const hasRequiredEmployment = hasRequiredEmploymentValues({
    canSelectSimflexityEmployee,
    canSelectSimflexityPay,
    formData,
    requirements,
    validationValues,
  });

  return {
    hasRequiredEmploymentValues: hasRequiredEmployment,
    hasRequiredPersonalValues: hasRequiredPersonalValues(formData),
    hasValidAgreement: validationValues.hasValidAgreement,
    hasValidEmployeeNumber: validationValues.hasValidEmployeeNumber,
    hasValidEmail: hasValidOptionalEmail({ value: formData.email }),
    hasValidFirstName: hasValidName({ value: formData.firstName }),
    hasValidLastName: hasValidName({ value: formData.lastName }),
    hasValidNoticeDays: validationValues.hasValidNoticeDays,
    hasValidPaymentMethod: validationValues.hasValidPaymentMethod,
    hasValidPersonalIdentityNumber: hasValidOptionalPersonalIdentityNumber({
      value: formData.personalIdentityNumber,
    }),
    hasValidRole: hasValidRequiredText({ value: formData.role }),
    hasValidSalaryAmount: validationValues.hasValidSalaryAmount,
    hasValidVacationDays: validationValues.hasValidVacationDays,
    hasValidWorkPercentage: validationValues.hasValidWorkPercentage,
    hasValidWorkplace: validationValues.hasValidWorkplace,
  };
}
